import { addToast } from '@heroui/react';
import { isAxiosError } from 'axios';
import { $api } from '@/shared/api/api';
import type { FileInfo } from '@/shared/types/files';

/**
 * Приватные файлы открываются по ключу через единый роут бэкенда: он сам находит запись,
 * которой принадлежит файл, и проверяет доступ к ней. Бэкенд: docs/FRONTEND_PRIVATE_FILES.md.
 */
const DOWNLOAD_PATH = '/private-files/download';

type Disposition = 'inline' | 'attachment';

/** Ссылка из ответа ещё действует. */
function hasLiveUrl(file: FileInfo): file is FileInfo & { url: string } {
  return Boolean(file.url && file.urlExpiresAt && Date.parse(file.urlExpiresAt) > Date.now());
}

/** Превью картинки: миниатюра, если есть, иначе оригинал; null — превью нет. */
export function getFilePreviewUrl(file: FileInfo): string | null {
  return hasLiveUrl(file) ? (file.thumbnailUrl ?? file.url) : null;
}

const FILE_UNAVAILABLE = 'The file is not available. Refresh the page and try again.';

/** Ссылка на 5 минут. 403 — файл чужой или больше не числится в записи, 404 — удалён. */
async function requestPrivateFileUrl(key: string, disposition: Disposition): Promise<string> {
  try {
    const { data } = await $api.get<{ data?: { url?: string } }>(DOWNLOAD_PATH, {
      params: { key, disposition },
    });
    if (!data?.data?.url) throw new Error(FILE_UNAVAILABLE);
    return data.data.url;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.status === 404 ? 'File not found' : FILE_UNAVAILABLE);
    }
    throw error;
  }
}

/**
 * Ссылка для плеера или <img>: свежий url из ответа, иначе — запрос к роуту (inline).
 * Бросает ошибку, если файл недоступен.
 */
export async function getPrivateFileUrl(file: FileInfo): Promise<string> {
  return hasLiveUrl(file) ? file.url : requestPrivateFileUrl(file.key, 'inline');
}

function showFileError(error: unknown): void {
  addToast({
    title: error instanceof Error ? error.message : 'Unable to open the file. Please try again.',
    color: 'danger',
  });
}

/** «Открыть»: во вкладке. Картинка со свежей ссылкой открывается без запроса. */
export async function viewPrivateFile(file: FileInfo): Promise<void> {
  if (hasLiveUrl(file)) {
    window.open(file.url, '_blank', 'noopener,noreferrer');
    return;
  }
  // Вкладку открываем синхронно в обработчике клика, иначе её заблокирует блокировщик всплывающих окон
  const tab = window.open('', '_blank');
  try {
    const url = await requestPrivateFileUrl(file.key, 'inline');
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch (error) {
    tab?.close();
    showFileError(error);
  }
}

/** «Скачать»: сохраняется под исходным именем, страница остаётся на месте. */
export async function downloadPrivateFile(file: FileInfo): Promise<void> {
  try {
    window.location.href = await requestPrivateFileUrl(file.key, 'attachment');
  } catch (error) {
    showFileError(error);
  }
}
